import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Palette, Type as TypeIcon, LayoutList, ListTree, Eye, Save, Rocket, Undo2,
  GripVertical, Plus, Trash2, Monitor, Smartphone, ExternalLink, Loader2,
} from "lucide-react";
import { api } from "@/lib/api";
import { DEFAULT_CONFIG, mergeConfig, FONT_OPTIONS } from "@/config/defaults";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PANELS = [
  { id: "theme", label: "Theme", icon: Palette },
  { id: "content", label: "Content", icon: TypeIcon },
  { id: "sections", label: "Sections", icon: LayoutList },
  { id: "lists", label: "Lists", icon: ListTree },
];
const SCREENS = ["home", "chat", "ai", "providers", "tracking", "done"];
const labelize = (s) => s.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());

function Field({ label, value, onChange }) {
  const long = (value || "").length > 55;
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-neutral-500">{labelize(label)}</span>
      {long
        ? <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="rounded-lg text-sm" />
        : <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-10 rounded-lg text-sm" />}
    </label>
  );
}

function Color({ label, value, onChange }) {
  return (
    <div>
      <span className="mb-1 block text-xs font-medium text-neutral-500">{label}</span>
      <div className="flex items-center gap-2">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-11 cursor-pointer rounded border border-black/10 bg-transparent" />
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-9 flex-1 rounded-lg font-mono-tech text-xs uppercase" />
      </div>
    </div>
  );
}

function StringListEditor({ items, onChange, placeholder = "Item" }) {
  return (
    <div className="space-y-2">
      {items.map((v, i) => (
        <div key={i} className="flex gap-2">
          <Input value={v} onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} className="h-10 rounded-lg text-sm" placeholder={placeholder} />
          <Button size="icon" variant="outline" className="h-10 w-10 shrink-0 rounded-lg" onClick={() => onChange(items.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-sos" /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" className="h-9 gap-1 rounded-lg" onClick={() => onChange([...items, ""])}><Plus className="h-4 w-4" /> Add</Button>
    </div>
  );
}

export default function SiteEditor() {
  const [cfg, setCfg] = useState(DEFAULT_CONFIG);
  const [panel, setPanel] = useState("theme");
  const [device, setDevice] = useState("desktop");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const iframeRef = useRef(null);
  const dragIndex = useRef(null);

  useEffect(() => {
    api.get("/admin/site-config").then((r) => {
      const base = r.data.has_draft ? r.data.draft : r.data.published;
      setCfg(mergeConfig(DEFAULT_CONFIG, base));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // push working config to preview iframe (debounced)
  useEffect(() => {
    const id = setTimeout(() => {
      iframeRef.current?.contentWindow?.postMessage({ __sosConfig: cfg }, "*");
    }, 200);
    return () => clearTimeout(id);
  }, [cfg]);

  useEffect(() => {
    const onMsg = (e) => {
      if (e.data?.__sosPreviewReady) iframeRef.current?.contentWindow?.postMessage({ __sosConfig: cfg }, "*");
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [cfg]);

  const update = (fn) => setCfg((prev) => { const next = structuredClone(prev); fn(next); return next; }) || setDirty(true);
  const edit = (fn) => { update(fn); setDirty(true); };

  const saveDraft = async () => {
    setSaving(true);
    try { await api.put("/admin/site-config/draft", { data: cfg }); setDirty(false); toast.success("Draft saved"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const publish = async () => {
    setSaving(true);
    try { await api.post("/admin/site-config/publish", { data: cfg }); setDirty(false); toast.success("Published — your site is live"); }
    catch { toast.error("Publish failed"); } finally { setSaving(false); }
  };
  const discard = async () => {
    setSaving(true);
    try {
      const r = await api.post("/admin/site-config/discard");
      setCfg(mergeConfig(DEFAULT_CONFIG, r.data.data)); setDirty(false); toast.message("Reverted to the published version");
    } catch { toast.error("Discard failed"); } finally { setSaving(false); }
  };

  const previewSrc = useMemo(() => `/?preview=1&ts=${Date.now()}`, []);

  if (loading) return <div className="py-16 text-center text-neutral-400">Loading site editor…</div>;

  return (
    <div className="grid gap-5 lg:grid-cols-[440px_1fr]" data-testid="site-editor">
      {/* editor panel */}
      <div className="flex flex-col rounded-2xl border border-black/5 bg-white">
        <div className="flex items-center justify-between gap-2 border-b border-black/5 p-3">
          <div className="flex gap-1">
            {PANELS.map((p) => (
              <button key={p.id} onClick={() => setPanel(p.id)}
                className={`flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold transition-colors ${panel === p.id ? "bg-brand text-white" : "text-neutral-500 hover:bg-black/5"}`}
                data-testid={`editor-panel-${p.id}`}><p.icon className="h-3.5 w-3.5" />{p.label}</button>
            ))}
          </div>
        </div>

        <div className="max-h-[62vh] space-y-5 overflow-y-auto p-4">
          {panel === "theme" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Color label="Brand" value={cfg.theme.brand} onChange={(v) => edit((n) => { n.theme.brand = v; })} />
                <Color label="Brand dark" value={cfg.theme.brandDark} onChange={(v) => edit((n) => { n.theme.brandDark = v; })} />
                <Color label="AI / success" value={cfg.theme.ai} onChange={(v) => edit((n) => { n.theme.ai = v; })} />
                <Color label="Emergency" value={cfg.theme.sos} onChange={(v) => edit((n) => { n.theme.sos = v; })} />
              </div>
              <div className="grid grid-cols-1 gap-3">
                {[["headingFont", "Heading font"], ["bodyFont", "Body font"]].map(([k, lbl]) => (
                  <div key={k}>
                    <span className="mb-1 block text-xs font-medium text-neutral-500">{lbl}</span>
                    <Select value={cfg.theme[k]} onValueChange={(v) => edit((n) => { n.theme[k] = v; })}>
                      <SelectTrigger className="h-10 rounded-lg text-sm" data-testid={`editor-font-${k}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{FONT_OPTIONS.map((f) => <SelectItem key={f} value={f} style={{ fontFamily: f }}>{f}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </>
          )}

          {panel === "content" && Object.entries(cfg.t).map(([sec, fields]) => (
            <div key={sec} className="rounded-xl border border-black/5 bg-[#FAFAFC] p-3">
              <p className="mb-2.5 text-sm font-bold text-neutral-800">{labelize(sec)}</p>
              <div className="space-y-2.5">
                {Object.entries(fields).map(([k, v]) => (
                  <Field key={k} label={k} value={v} onChange={(val) => edit((n) => { n.t[sec][k] = val; })} />
                ))}
              </div>
            </div>
          ))}

          {panel === "sections" && (
            <div className="space-y-2">
              <p className="mb-1 text-xs text-neutral-500">Drag to reorder. Toggle to show or hide a section on the page.</p>
              {cfg.sections.map((s, i) => (
                <div key={s.id} draggable
                  onDragStart={() => (dragIndex.current = i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => edit((n) => { const a = n.sections; const [m] = a.splice(dragIndex.current, 1); a.splice(i, 0, m); })}
                  className="flex items-center gap-3 rounded-xl border border-black/5 bg-white p-3"
                  data-testid={`editor-section-${s.id}`}>
                  <GripVertical className="h-4 w-4 cursor-grab text-neutral-300" />
                  <span className="flex-1 text-sm font-medium text-neutral-800">{s.label}</span>
                  <Switch checked={s.visible} onCheckedChange={(v) => edit((n) => { n.sections[i].visible = v; })} data-testid={`editor-section-toggle-${s.id}`} />
                </div>
              ))}
            </div>
          )}

          {panel === "lists" && (
            <div className="space-y-6">
              <ListBlock title="FAQ">
                {cfg.lists.faqs.map((f, i) => (
                  <ObjItem key={i} onRemove={() => edit((n) => { n.lists.faqs.splice(i, 1); })}>
                    <Input value={f.q} onChange={(e) => edit((n) => { n.lists.faqs[i].q = e.target.value; })} className="h-10 rounded-lg text-sm font-medium" placeholder="Question" />
                    <Textarea value={f.a} onChange={(e) => edit((n) => { n.lists.faqs[i].a = e.target.value; })} rows={2} className="rounded-lg text-sm" placeholder="Answer" />
                  </ObjItem>
                ))}
                <AddBtn onClick={() => edit((n) => { n.lists.faqs.push({ q: "New question", a: "Answer" }); })} />
              </ListBlock>

              <ListBlock title="Features">
                {cfg.lists.features.map((f, i) => (
                  <ObjItem key={i} onRemove={() => edit((n) => { n.lists.features.splice(i, 1); })}>
                    <Input value={f.title} onChange={(e) => edit((n) => { n.lists.features[i].title = e.target.value; })} className="h-10 rounded-lg text-sm font-medium" placeholder="Title" />
                    <Textarea value={f.body} onChange={(e) => edit((n) => { n.lists.features[i].body = e.target.value; })} rows={2} className="rounded-lg text-sm" placeholder="Description" />
                    <Input value={f.tag} onChange={(e) => edit((n) => { n.lists.features[i].tag = e.target.value; })} className="h-9 rounded-lg text-xs" placeholder="Tag (AI / Live / blank)" />
                  </ObjItem>
                ))}
                <AddBtn onClick={() => edit((n) => { n.lists.features.push({ title: "New feature", body: "", tag: "" }); })} />
              </ListBlock>

              <ListBlock title="How-It-Works Steps">
                {cfg.lists.steps.map((s, i) => (
                  <ObjItem key={i} onRemove={() => edit((n) => { n.lists.steps.splice(i, 1); })}>
                    <Input value={s.title} onChange={(e) => edit((n) => { n.lists.steps[i].title = e.target.value; })} className="h-10 rounded-lg text-sm font-medium" placeholder="Step title" />
                    <Textarea value={s.body} onChange={(e) => edit((n) => { n.lists.steps[i].body = e.target.value; })} rows={2} className="rounded-lg text-sm" placeholder="Step description" />
                    <Select value={s.screen} onValueChange={(v) => edit((n) => { n.lists.steps[i].screen = v; })}>
                      <SelectTrigger className="h-9 rounded-lg text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{SCREENS.map((sc) => <SelectItem key={sc} value={sc}>{sc}</SelectItem>)}</SelectContent>
                    </Select>
                  </ObjItem>
                ))}
                <AddBtn onClick={() => edit((n) => { n.lists.steps.push({ title: "New step", body: "", screen: "home" }); })} />
              </ListBlock>

              <ListBlock title="Services"><StringListEditor items={cfg.lists.services} onChange={(v) => edit((n) => { n.lists.services = v; })} /></ListBlock>
              <ListBlock title="Problems"><StringListEditor items={cfg.lists.problems} onChange={(v) => edit((n) => { n.lists.problems = v; })} /></ListBlock>
              <ListBlock title="Provider Benefits"><StringListEditor items={cfg.lists.providerBenefits} onChange={(v) => edit((n) => { n.lists.providerBenefits = v; })} /></ListBlock>
              <ListBlock title="Marquee words"><StringListEditor items={cfg.lists.marquee} onChange={(v) => edit((n) => { n.lists.marquee = v; })} /></ListBlock>
              <ListBlock title="Comparison — Old way"><StringListEditor items={cfg.lists.comparison.old} onChange={(v) => edit((n) => { n.lists.comparison.old = v; })} /></ListBlock>
              <ListBlock title="Comparison — SOSConnect AI"><StringListEditor items={cfg.lists.comparison.new} onChange={(v) => edit((n) => { n.lists.comparison.new = v; })} /></ListBlock>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-black/5 p-3">
          <Button onClick={publish} disabled={saving} className="flex-1 gap-1.5 rounded-lg bg-brand hover:bg-brand-dark" data-testid="editor-publish">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />} Publish
          </Button>
          <Button onClick={saveDraft} disabled={saving} variant="outline" className="gap-1.5 rounded-lg" data-testid="editor-save-draft"><Save className="h-4 w-4" /> Save draft</Button>
          <Button onClick={discard} disabled={saving} variant="ghost" className="gap-1.5 rounded-lg text-neutral-500" data-testid="editor-discard"><Undo2 className="h-4 w-4" /> Discard</Button>
          {dirty && <span className="text-xs font-medium text-amber-600">Unsaved changes</span>}
        </div>
      </div>

      {/* live preview */}
      <div className="overflow-hidden rounded-2xl border border-black/5 bg-neutral-100">
        <div className="flex items-center justify-between border-b border-black/5 bg-white px-4 py-2.5">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-700"><Eye className="h-4 w-4 text-brand" /> Live preview</div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg bg-neutral-100 p-0.5">
              <button onClick={() => setDevice("desktop")} className={`grid h-8 w-8 place-items-center rounded-md ${device === "desktop" ? "bg-white shadow-sm text-brand" : "text-neutral-400"}`} data-testid="editor-device-desktop"><Monitor className="h-4 w-4" /></button>
              <button onClick={() => setDevice("mobile")} className={`grid h-8 w-8 place-items-center rounded-md ${device === "mobile" ? "bg-white shadow-sm text-brand" : "text-neutral-400"}`} data-testid="editor-device-mobile"><Smartphone className="h-4 w-4" /></button>
            </div>
            <a href="/?preview=1" target="_blank" rel="noreferrer" className="grid h-8 w-8 place-items-center rounded-md text-neutral-400 hover:bg-black/5"><ExternalLink className="h-4 w-4" /></a>
          </div>
        </div>
        <div className="flex justify-center overflow-auto p-3" style={{ height: "70vh" }}>
          <iframe ref={iframeRef} title="Live preview" src={previewSrc}
            className="h-full border-0 bg-white shadow-sm transition-all duration-300"
            style={{ width: device === "mobile" ? 390 : "100%", borderRadius: device === "mobile" ? 24 : 12 }} />
        </div>
      </div>
    </div>
  );
}

function ListBlock({ title, children }) {
  return (
    <div className="rounded-xl border border-black/5 bg-[#FAFAFC] p-3">
      <p className="mb-2.5 text-sm font-bold text-neutral-800">{title}</p>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}
function ObjItem({ children, onRemove }) {
  return (
    <div className="relative space-y-2 rounded-lg border border-black/5 bg-white p-2.5 pr-9">
      {children}
      <button onClick={onRemove} className="absolute right-2 top-2 text-neutral-300 hover:text-sos"><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}
function AddBtn({ onClick }) {
  return <Button size="sm" variant="outline" className="h-9 gap-1 rounded-lg" onClick={onClick}><Plus className="h-4 w-4" /> Add</Button>;
}
